import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ErrorState from '../components/ui/ErrorState'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import queueService from '../services/queueService'
import publicRecognitionAdminService from '../services/publicRecognitionAdminService'
import { useAuthStore } from '../services/authStore'
import { useTranslation } from '../contexts/TranslationContext'
import { ui } from '../styles/ui'
import { formatCurrency } from '../utils/currency'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'

function createEmptyQueueSummary() {
  return {
    pending: null,
    processing: null,
    completed: null,
    failed: null,
  }
}

function createEmptyReceiptSummary() {
  return {
    totalCount: 0,
    monthlyCount: 0,
    average: 0,
    currency: 'EUR',
  }
}

function createEmptyRecognitionSummary() {
  return {
    total: 0,
    successCount: 0,
    failureCount: 0,
    publicRecognitionCount: 0,
    telegramRecognitionCount: 0,
    averageDurationMs: null,
    successRate: null,
    bySource: { image: 0, qr: 0, speech: 0 },
    byStatus: { pending: 0, processing: 0, completed: 0, failed: 0 },
    byEndpointGroup: {
      public_recognition: 0,
      telegram_mini_app_recognition: 0,
      unknown: 0,
    },
  }
}

function createEmptyMetricsSnapshot() {
  return {
    generatedAt: null,
    systemHealth: 'idle',
    queues: [],
    summary: createEmptyQueueSummary(),
    receiptSummary: createEmptyReceiptSummary(),
    recognitionSummary: createEmptyRecognitionSummary(),
    pipelines: [],
    recentJobs: [],
    lastErrors: [],
    activeAlerts: [],
    dataSummary: {
      totalReceipts: 0,
      receiptsThisMonth: 0,
      completedProcessingCount: 0,
      failedProcessingCount: 0,
      averageReceiptAmount: 0,
    },
    warnings: [],
  }
}

const PIPELINE_BADGE_CLASS = {
  ok: 'rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700',
  warning: 'rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700',
  failing: 'rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-red-700',
  idle: 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
}

function getPipelineStatusClass(status) {
  return PIPELINE_BADGE_CLASS[status] || PIPELINE_BADGE_CLASS.idle
}

export default function Metrics() {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  const [metrics, setMetrics] = useState(null)
  const [publicMetrics, setPublicMetrics] = useState(null)
  const [loadWarnings, setLoadWarnings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchMetrics = async () => {
    try {
      setLoading(true)
      setError('')
      setMetrics(null)
      setPublicMetrics(null)
      setLoadWarnings([])

      const [metricsResponse, publicResponse] = await Promise.allSettled([
        queueService.adminMetrics(),
        publicRecognitionAdminService.stats(),
      ])

      const failedRequests = []
      if (metricsResponse.status === 'fulfilled') {
        setMetrics(metricsResponse.value || null)
      } else {
        failedRequests.push('admin_metrics')
      }

      if (publicResponse.status === 'fulfilled') {
        setPublicMetrics(publicResponse.value || null)
      } else {
        failedRequests.push('public_recognition')
      }

      if (failedRequests.length === 2) {
        setError(t('Unable to load metrics.', 'Unable to load metrics.'))
      } else if (failedRequests.length > 0) {
        setLoadWarnings(failedRequests)
        console.warn('[metrics] partial load failure', { failedRequests })
      }
    } catch (err) {
      console.error('Failed to load metrics', err)
      setError(t('Unable to load metrics.', 'Unable to load metrics.'))
    } finally {
      setLoading(false)
    }
  }

  if (!user?.is_admin) {
    return (
      <PageShell>
        <Card>
          <p className="text-sm text-foreground">
            {t('You do not have permission to view this page.', 'You do not have permission to view this page.')}
          </p>
        </Card>
      </PageShell>
    )
  }

  const snapshot = metrics || createEmptyMetricsSnapshot()
  const queueSummary = snapshot.summary || createEmptyQueueSummary()
  const receiptSummary = snapshot.receiptSummary || snapshot.dataSummary || createEmptyReceiptSummary()
  const recognitionSummary = publicMetrics || snapshot.recognitionSummary || createEmptyRecognitionSummary()
  const pipelineRows = Array.isArray(snapshot.pipelines) ? snapshot.pipelines : []
  const warnings = [
    ...(Array.isArray(snapshot.warnings) ? snapshot.warnings : []),
    ...loadWarnings,
  ]

  return (
    <PageShell>
      <PageHeader
        title={t('Recognition Metrics', 'Recognition Metrics')}
        subtitle={t('Queue health, receipt throughput, and public/TG recognition.', 'Queue health, receipt throughput, and public/TG recognition.')}
        actions={<Button variant="secondary" onClick={fetchMetrics}>{t('Refresh', 'Refresh')}</Button>}
      />

      {snapshot.generatedAt && (
        <p className="text-xs text-muted-foreground">
          {t('Updated at', 'Updated at')}: {formatDateTime(snapshot.generatedAt)}
        </p>
      )}

      {error && <ErrorState text={error} />}

      {warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-800">
            {t('Partial data loaded.', 'Partial data loaded.')}
            {' '}
            {warnings.join(', ')}
          </p>
        </Card>
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card>
              <h2 className="mb-4 text-base font-semibold text-foreground">{t('Queue Health', 'Queue Health')}</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Pending', 'Pending')}</p>
                  <p className="mt-1 text-3xl font-bold text-foreground">{formatEmptyValue(queueSummary.pending)}</p>
                </div>
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Processing', 'Processing')}</p>
                  <p className="mt-1 text-3xl font-bold text-amber-700">{formatEmptyValue(queueSummary.processing)}</p>
                </div>
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Completed', 'Completed')}</p>
                  <p className="mt-1 text-3xl font-bold text-emerald-700">{formatEmptyValue(queueSummary.completed)}</p>
                </div>
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Failed', 'Failed')}</p>
                  <p className="mt-1 text-3xl font-bold text-red-700">{formatEmptyValue(queueSummary.failed)}</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                {t('Queues tracked', 'Queues tracked')}: {snapshot.queues?.length ?? 0}
              </div>
            </Card>

            <Card>
              <h2 className="mb-4 text-base font-semibold text-foreground">{t('Receipt Activity', 'Receipt Activity')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Total receipts', 'Total receipts')}</p>
                  <p className="mt-1 text-3xl font-bold text-foreground">{receiptSummary.totalCount ?? 0}</p>
                </div>
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('This month', 'This month')}</p>
                  <p className="mt-1 text-3xl font-bold text-sky-700">{receiptSummary.monthlyCount ?? 0}</p>
                </div>
                <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Average', 'Average')}</p>
                  <p className="mt-1 text-2xl font-bold text-foreground">
                    {formatCurrency(receiptSummary.average ?? 0, receiptSummary.currency || 'EUR')}
                  </p>
                </div>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                {t('System health', 'System health')}: {formatEmptyValue(snapshot.systemHealth)}
              </div>
            </Card>
          </div>

          <Card>
            <h2 className="mb-4 text-base font-semibold text-foreground">{t('Public & TG Recognition', 'Public & TG Recognition')}</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Total jobs', 'Total jobs')}</p>
                <p className="mt-1 text-3xl font-bold text-foreground">{recognitionSummary.total ?? 0}</p>
              </div>
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Public API', 'Public API')}</p>
                <p className="mt-1 text-3xl font-bold text-sky-700">{recognitionSummary.publicRecognitionCount ?? 0}</p>
              </div>
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Telegram', 'Telegram')}</p>
                <p className="mt-1 text-3xl font-bold text-violet-700">{recognitionSummary.telegramRecognitionCount ?? 0}</p>
              </div>
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Success rate', 'Success rate')}</p>
                <p className="mt-1 text-3xl font-bold text-foreground">
                  {recognitionSummary.successRate == null ? formatEmptyValue(null) : `${Math.round(recognitionSummary.successRate * 100)}%`}
                </p>
              </div>
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Avg duration', 'Avg duration')}</p>
                <p className="mt-1 text-3xl font-bold text-foreground">
                  {recognitionSummary.averageDurationMs == null
                    ? formatEmptyValue(null)
                    : `${Math.round(recognitionSummary.averageDurationMs)} ${t('ms', 'ms')}`}
                </p>
              </div>
              <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Completed', 'Completed')}</p>
                <p className="mt-1 text-3xl font-bold text-emerald-700">{recognitionSummary.successCount ?? 0}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-outline px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Images', 'Images')}</p>
                <p className="mt-1 text-foreground">{recognitionSummary.bySource?.image ?? 0}</p>
              </div>
              <div className="rounded-lg border border-outline px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('QR', 'QR')}</p>
                <p className="mt-1 text-foreground">{recognitionSummary.bySource?.qr ?? 0}</p>
              </div>
              <div className="rounded-lg border border-outline px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Speech', 'Speech')}</p>
                <p className="mt-1 text-foreground">{recognitionSummary.bySource?.speech ?? 0}</p>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="mb-4 text-base font-semibold text-foreground">{t('Realtime Pipelines', 'Realtime Pipelines')}</h2>
            {pipelineRows.length === 0 ? (
              <p className="text-sm italic text-muted-foreground">
                {t('No realtime pipeline data available.', 'No realtime pipeline data available.')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className={ui.tables.table}>
                  <thead className={ui.tables.head}>
                    <tr>
                      <th className={ui.tables.headCell}>{t('Pipeline', 'Pipeline')}</th>
                      <th className={ui.tables.headCell}>{t('Status', 'Status')}</th>
                      <th className={`${ui.tables.headCell} text-right`}>{t('Waiting', 'Waiting')}</th>
                      <th className={`${ui.tables.headCell} text-right`}>{t('Active', 'Active')}</th>
                      <th className={`${ui.tables.headCell} text-right`}>{t('Avg ms', 'Avg ms')}</th>
                      <th className={ui.tables.headCell}>{t('Last run', 'Last run')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-surface text-sm text-foreground">
                    {pipelineRows.map((pipeline) => (
                      <tr key={pipeline.pipelineName || pipeline.displayName}>
                        <td className={ui.tables.cell}>
                          <div className="font-medium text-foreground">{pipeline.displayName || pipeline.pipelineName}</div>
                        </td>
                        <td className={ui.tables.cell}>
                          <span className={getPipelineStatusClass(pipeline.status)}>
                            {String(pipeline.status || 'idle').toUpperCase()}
                          </span>
                        </td>
                        <td className={`${ui.tables.cell} text-right`}>{formatEmptyValue(pipeline.metadata?.waitingCount)}</td>
                        <td className={`${ui.tables.cell} text-right`}>{formatEmptyValue(pipeline.metadata?.activeCount)}</td>
                        <td className={`${ui.tables.cell} text-right`}>
                          {pipeline.avgProcessingMs == null ? formatEmptyValue(null) : Math.round(pipeline.avgProcessingMs)}
                        </td>
                        <td className={ui.tables.cell}>{pipeline.lastRunAt ? formatDateTime(pipeline.lastRunAt) : formatEmptyValue(null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </PageShell>
  )
}
